// Card surfaces, media cards and rails (UX02). Owner: WP04.
// Cards use a "stretched link" on the title so the whole card is clickable while the action stays a separate control.
import { Children, useEffect, useRef, useState, type ElementType, type ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Artwork } from './Artwork.tsx';
import { LiveBadge, ProgressBar } from './Badge.tsx';
import { IconButton } from './Button.tsx';

export function Card({ as: Tag = 'div', className, children, padded = true, ...rest }: { as?: ElementType; className?: string; children: ReactNode; padded?: boolean } & Record<string, unknown>) {
  return <Tag className={['ui-card', padded ? 'ui-card--padded' : '', className ?? ''].filter(Boolean).join(' ')} {...rest}>{children}</Tag>;
}

type CardProgress = { value: number; label: string; valueText?: string };
function CardTitle({ to, onOpen, children }: { to?: string; onOpen?: () => void; children: ReactNode }) {
  if (to) return <Link className="ui-card__link" to={to}>{children}</Link>;
  if (onOpen) return <button type="button" className="ui-card__link" onClick={onOpen}>{children}</button>;
  return <>{children}</>;
}

export type MediaCardProps = {
  title: string;
  subtitle?: ReactNode;
  artworkId?: string | null;
  /** Fallback label/colour seed (defaults to title). */
  artworkLabel?: string;
  /** Channel logo shown top-right (live cards). */
  logoId?: string | null;
  logoLabel?: string;
  live?: boolean;
  badge?: ReactNode;
  progress?: CardProgress;
  /** Where the card goes when activated (detail page). */
  to?: string;
  /** Alternative to `to`: select in place (e.g. open a sheet). */
  onOpen?: () => void;
  /** One action control (Button) shown bottom-right, e.g. Resume / Watch live. */
  action?: ReactNode;
  headingLevel?: 2 | 3 | 4;
  className?: string;
};

/** Landscape 16:9 card with overlaid title, progress and a single action (01_Home). */
export function MediaCard({ title, subtitle, artworkId, artworkLabel, logoId, logoLabel, live, badge, progress, to, onOpen, action, headingLevel = 3, className }: MediaCardProps) {
  const H = `h${headingLevel}` as 'h3';
  return (
    <article className={['ui-media', className ?? ''].filter(Boolean).join(' ')}>
      <Artwork id={artworkId} alt="" label={artworkLabel ?? title} ratio="16/9" className="ui-media__art" bare={!!logoLabel} />
      <div className="ui-media__scrim" aria-hidden />
      <div className="ui-media__top">
        <span className="ui-media__badges">{live ? <LiveBadge /> : null}{badge}</span>
        {logoLabel && logoId !== undefined ? <Artwork id={logoId} alt={logoLabel} label={logoLabel} fit="contain" className="ui-media__logo" /> : null}
      </div>
      <div className="ui-media__body">
        <div className="ui-media__text">
          <H className="ui-media__title"><CardTitle to={to} onOpen={onOpen}>{title}</CardTitle></H>
          {subtitle ? <p className="ui-media__subtitle">{subtitle}</p> : null}
          {progress ? <ProgressBar {...progress} className="ui-media__progress" /> : null}
        </div>
        {action ? <div className="ui-media__action">{action}</div> : null}
      </div>
    </article>
  );
}

export type PosterCardProps = {
  title: string;
  meta?: ReactNode;
  posterId?: string | null;
  to?: string;
  onOpen?: () => void;
  badge?: ReactNode;
  progress?: CardProgress;
  /** One control under the meta line (e.g. Remove, Play); stays separately focusable. */
  action?: ReactNode;
  headingLevel?: 2 | 3 | 4;
  className?: string;
};

/** Portrait 2:3 poster with title and meta line below (03_Movie_Detail "More like this"). */
export function PosterCard({ title, meta, posterId, to, onOpen, badge, progress, action, headingLevel = 3, className }: PosterCardProps) {
  const H = `h${headingLevel}` as 'h3';
  return (
    <article className={['ui-poster', className ?? ''].filter(Boolean).join(' ')}>
      <div className="ui-poster__frame">
        <Artwork id={posterId} alt="" label={title} ratio="2/3" />
        {badge ? <span className="ui-poster__badge">{badge}</span> : null}
        {progress ? <ProgressBar {...progress} className="ui-poster__progress" /> : null}
      </div>
      <H className="ui-poster__title"><CardTitle to={to} onOpen={onOpen}>{title}</CardTitle></H>
      {meta ? <p className="ui-poster__meta">{meta}</p> : null}
      {action ? <div className="ui-poster__action">{action}</div> : null}
    </article>
  );
}

export type RailProps = {
  title: string;
  /** Heading id (for aria-labelledby); auto from title when omitted. */
  id?: string;
  seeAllTo?: string;
  seeAllLabel?: string;
  /** Extra controls next to the heading (e.g. filter chips). */
  headerExtra?: ReactNode;
  /** Visible columns on wide screens (cards shrink to fit; the rest scroll). */
  columns?: number;
  children: ReactNode;
};

/** Horizontal, keyboard-reachable list of cards with a heading, optional "See all" and scroll buttons. */
export function Rail({ title, id, seeAllTo, seeAllLabel = 'See all', headerExtra, columns = 4, children }: RailProps) {
  const listRef = useRef<HTMLUListElement>(null);
  const [edges, setEdges] = useState({ start: true, end: true });
  const headingId = id ?? `rail-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const update = () => setEdges({ start: el.scrollLeft <= 1, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1 });
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => { el.removeEventListener('scroll', update); ro?.disconnect(); };
  }, []);
  const scroll = (dir: 1 | -1) => listRef.current?.scrollBy({ left: dir * listRef.current.clientWidth * 0.9, behavior: 'smooth' });
  return (
    <section className="section ui-rail" aria-labelledby={headingId}>
      <div className="section-header">
        <div>
          <h2 id={headingId}>{title}</h2>
          {headerExtra}
        </div>
        <div className="row">
          {!(edges.start && edges.end) ? (
            <span className="ui-rail__nav">
              <IconButton label={`Scroll ${title} back`} icon={<ChevronLeft size={20} />} onClick={() => scroll(-1)} disabled={edges.start} size="sm" />
              <IconButton label={`Scroll ${title} forward`} icon={<ChevronRight size={20} />} onClick={() => scroll(1)} disabled={edges.end} size="sm" />
            </span>
          ) : null}
          {seeAllTo ? <Link className="see-all" to={seeAllTo}>{seeAllLabel} <ChevronRight size={16} aria-hidden /></Link> : null}
        </div>
      </div>
      <ul className="ui-rail__list" ref={listRef} style={{ ['--rail-cols' as string]: columns }}>
        {Children.map(children, (child) => (child ? <li>{child}</li> : null))}
      </ul>
    </section>
  );
}
